import { ApolloProvider } from '@apollo/client';
import { ErrorBoundary as RollbarErrorBoundary, Provider as RollbarProvider, useRollbar } from '@rollbar/react';
import React, { useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import ReactNotification from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';
import { ErrorBoundaryFallbackUI } from './src/components/ErrorBoundaryFallbackUI/ErrorBoundaryFallbackUI';
import client from './src/utils/client';
import Common from './src/utils/Common';
import config from './src/utils/config';
import { rollbarScrubOptions } from './src/utils/telemetrySanitizer';
import { BUILD_REFRESH_USER_IDLE_MS, getBuildRefreshUrl, isValidBuildSha, shouldApplyQueuedReload, shouldQueueReloadForSha } from './src/utils/buildRefresh';

const inWindow = typeof window !== 'undefined';
const BUILD_SHA = String(process.env.GATSBY_BUILD_SHA || 'unknown');
const STORAGE_KEY = 'collar-app-web-build-sha';
const REFRESH_INTERVAL_MS = 2 * 60 * 1000;
const IDLE_RELOAD_POLL_MS = 15 * 1000;

let hasQueuedBuildReload = false;
let queuedBuildSha = '';
let lastInteractionAt = Date.now();

const registerWebMcpContext = () => {
  if (!inWindow) return;
  const nav = navigator;
  const modelContext = (nav as Navigator & { modelContext?: { provideContext: (context: unknown) => void } }).modelContext;
  if (!modelContext?.provideContext) return;

  modelContext.provideContext({
    tools: [
      {
        name: 'search_providers',
        description: 'Navigate to providers directory to discover available businesses.',
        inputSchema: {
          type: 'object',
          properties: {
            country: {
              type: 'string',
              description: 'Optional country code path segment, e.g. uk or us.'
            }
          }
        },
        execute: async ({ country }: { country?: string }) => {
          const countrySegment = country ? `/${country}` : '';
          window.location.assign(`${countrySegment}/providers`);
          return { ok: true };
        }
      },
      {
        name: 'open_booking',
        description: 'Open a booking page for a given branch slug.',
        inputSchema: {
          type: 'object',
          required: ['branchSlug'],
          properties: {
            branchSlug: {
              type: 'string',
              description: 'Branch slug from /brands/{branchSlug}.'
            },
            country: {
              type: 'string',
              description: 'Optional country code path segment, e.g. uk or us.'
            }
          }
        },
        execute: async ({ branchSlug, country }: { branchSlug: string; country?: string }) => {
          const countrySegment = country ? `/${country}` : '';
          window.location.assign(`${countrySegment}/brands/${branchSlug}/book`);
          return { ok: true };
        }
      }
    ]
  });
};

const queueReloadForSha = (nextBuildSha: string) => {
  if (!inWindow) return false;
  try {
    if (
      shouldQueueReloadForSha({
        nextBuildSha,
        readLocalStorage: key => window.localStorage.getItem(key),
        writeLocalStorage: (key, value) => window.localStorage.setItem(key, value),
        readSessionStorage: key => window.sessionStorage.getItem(key),
        writeSessionStorage: (key, value) => window.sessionStorage.setItem(key, value),
        storageKey: STORAGE_KEY
      })
    ) {
      hasQueuedBuildReload = true;
      queuedBuildSha = nextBuildSha;
      return true;
    }
  } catch (error) {
    // Ignore storage access failures (private mode or blocked storage)
  }
  return false;
};

const maybeApplyQueuedReload = () => {
  if (!inWindow || !hasQueuedBuildReload) return false;
  if (
    shouldApplyQueuedReload({
      now: Date.now(),
      lastInteractionAt,
      visibilityState: document.visibilityState,
      userIdleMs: BUILD_REFRESH_USER_IDLE_MS
    })
  ) {
    window.location.replace(getBuildRefreshUrl(window.location.href, queuedBuildSha));
    return true;
  }
  return false;
};

const checkLocalBuildRefresh = () => {
  if (!inWindow || !isValidBuildSha(BUILD_SHA)) return false;
  queueReloadForSha(BUILD_SHA);
  return maybeApplyQueuedReload();
};

const checkRemoteBuildRefresh = async () => {
  if (!inWindow) return;
  try {
    const response = await window.fetch(`/build-sha.txt?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return;
    const remoteBuildSha = (await response.text()).trim();
    queueReloadForSha(remoteBuildSha);
    maybeApplyQueuedReload();
  } catch (error) {
    // Ignore network/transient failures.
  }
};

if (inWindow) {
  const markInteraction = () => {
    lastInteractionAt = Date.now();
  };

  checkLocalBuildRefresh();
  void checkRemoteBuildRefresh();
  registerWebMcpContext();
  window.setInterval(checkRemoteBuildRefresh, REFRESH_INTERVAL_MS);
  window.setInterval(maybeApplyQueuedReload, IDLE_RELOAD_POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      maybeApplyQueuedReload();
    } else void checkRemoteBuildRefresh();
  });
  window.addEventListener('blur', maybeApplyQueuedReload);
  window.addEventListener('pointerdown', markInteraction, { passive: true });
  window.addEventListener('keydown', markInteraction, { passive: true });
  window.addEventListener('scroll', markInteraction, { passive: true });
  window.addEventListener('mousemove', markInteraction, { passive: true });
  window.addEventListener('touchstart', markInteraction, { passive: true });
  import('firebase/auth');
  import('firebase/app');
  import('firebase/firestore');
  import('firebase/messaging');
  import('firebase/storage');
  import('firebase/analytics');
  import('firebase/remote-config');
}

const RollbarContent = ({ element }) => {
  const rollbar = useRollbar();
  useEffect(() => {
    Common.set('rollbar', rollbar);
    return () => {
      Common.clear('rollbar');
    };
  }, [rollbar]);

  return (
    <ApolloProvider client={client}>
      <ReactNotification />
      {element}
    </ApolloProvider>
  );
};

export const wrapRootElement = ({ element }) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.RECAPTCHA_SITE_KEY}>
      <RollbarProvider config={{ accessToken: config.ROLLBAR_KEY, environment: config.ROLLBAR_ENV, ...rollbarScrubOptions }}>
        <RollbarErrorBoundary fallbackUI={ErrorBoundaryFallbackUI}>
          <RollbarContent element={element} />
        </RollbarErrorBoundary>
      </RollbarProvider>
    </GoogleReCaptchaProvider>
  );
};
